import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@material-ui/core';

import Head from './Head';
import Body from './Body';
import useStyles from './useStyles';

/** Generic data grid: `columns` and `rows` are data, cell content is the caller's. */
const DataTable = ({
  columns,
  rows,
  sortBy,
  sortOrder,
  onSort,
  selectable,
  allSelected,
  onSelectAll,
  loading,
  emptyState,
  className,
  striped,
  dense,
}) => {
  const classes = useStyles();
  const colSpan = columns.length + (selectable ? 1 : 0);

  return (
    <>
      <TableContainer
        className={classnames(classes.container, striped && classes.containerStriped, className)}
      >
        <Table size="medium">
          <Head
            columns={columns}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={onSort}
            selectable={selectable}
            allSelected={allSelected}
            indeterminate={!allSelected && rows.some((row) => row.selected)}
            onSelectAll={onSelectAll}
            striped={striped}
            dense={dense}
          />
          {loading && !!rows.length && (
            <TableBody>
              <TableRow>
                <TableCell padding="none" colSpan={colSpan} className={classes.progress}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            </TableBody>
          )}
          {!!rows.length && (
            <Body
              columns={columns}
              rows={rows}
              selectable={selectable}
              striped={striped}
              dense={dense}
            />
          )}
        </Table>
      </TableContainer>
      {!rows.length && (
        <div className={classes.placeholder}>
          {loading ? <CircularProgress size={32} /> : emptyState}
        </div>
      )}
    </>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node,
      align: PropTypes.string,
      sortable: PropTypes.bool,
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      cells: PropTypes.arrayOf(
        PropTypes.shape({
          content: PropTypes.node,
          align: PropTypes.string,
          className: PropTypes.string,
          onClick: PropTypes.func,
        })
      ).isRequired,
      onClick: PropTypes.func,
      selected: PropTypes.bool,
    })
  ).isRequired,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.oneOf(['asc', 'desc']),
  onSort: PropTypes.func,
  selectable: PropTypes.bool,
  allSelected: PropTypes.bool,
  onSelectAll: PropTypes.func,
  loading: PropTypes.bool,
  emptyState: PropTypes.node,
  className: PropTypes.string,
  striped: PropTypes.bool,
  dense: PropTypes.bool,
};

DataTable.defaultProps = {
  sortBy: '',
  sortOrder: 'asc',
  onSort: undefined,
  selectable: false,
  allSelected: false,
  onSelectAll: undefined,
  loading: false,
  emptyState: 'No Results',
  className: undefined,
  striped: false,
  dense: false,
};

export default DataTable;
